import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const LanguageDropdown = ({ value, onChange, languages, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const currentLanguageLabel =
    languages.find((l) => l.value === value)?.label || languages[0]?.label || "";

  // Position menu when opening (fixed = viewport coords)
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 180),
      });
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (langValue) => {
    onChange(langValue);
    setIsOpen(false);
  };

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      className="fixed bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-[9999]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: "min(280px, 60vh)",
        overflowY: "auto",
      }}
    >
      {languages.map((lang) => (
        <button
          key={lang.value}
          type="button"
          onClick={() => handleSelect(lang.value)}
          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 first:rounded-t-lg last:rounded-b-lg flex items-center justify-between ${
            value === lang.value
              ? "bg-gray-100 dark:bg-gray-600 font-medium"
              : "text-gray-900 dark:text-gray-100"
          }`}
        >
          <span>{lang.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className={`relative ${className}`} ref={triggerRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="border border-gray-300 dark:border-gray-600 h-11 sm:h-10 rounded-full px-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 cursor-pointer flex items-center gap-2 min-w-[120px] max-w-full justify-between overflow-hidden"
        >
          <span className="truncate flex-1 min-w-0">{currentLanguageLabel}</span>
          <svg
            className={`w-4 h-4 transition-transform flex-shrink-0 pointer-events-none ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
      {typeof document !== "undefined" && createPortal(dropdownMenu, document.body)}
    </>
  );
};

export default LanguageDropdown;

